import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import ApproveRequiredTable from "./ApproveRequired";
import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import type {
  ApprovalRequiredTransactions,
  CountryInfo,
  PaymentType,
} from "@/app/types/payments";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";
import AppCustomDropDown from "@/app/components/common/actions/inputs-b/AppCustomDropDown";
import {
  gxGetCountryList,
  gxPaymentTypesApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { gxIsArray } from "@/app/utils/helpers";
import DialogXl from "@/app/models/dialogs/DialogXl";
import { useT } from "@/app/hooks/useT";
import { Loader } from "@progress/kendo-react-indicators";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";

interface ViewApprovalRequired {
  openApprovalRequiredDrawer: boolean;
  transactionType: string | null;
  country: string | null;
  searchApproval: string | undefined;
  selectedTransactionList: number[];
  tableListAll: ApprovalRequiredTransactions[];
  checkAll: boolean;
  loader: any;
  loading: boolean;
  hasMore: boolean;
  handleApprovalRequiredDrawer: () => void;
  handleTransactionType: (value: any) => void;
  handleCountry: (value: any) => void;
  setSearchApproval: (value: string) => void;
  setSelectedTransactionList: Dispatch<SetStateAction<number[]>>;
  handleRowClick: (value: GridRowClickEvent) => void;
  setCheckAll: Dispatch<SetStateAction<boolean>>;
  handleBulkReject: () => void;
  handleBulkApprove: () => void;
}

export default function ViewAllApprovalRequired({
  openApprovalRequiredDrawer,
  transactionType,
  country,
  searchApproval,
  selectedTransactionList,
  tableListAll,
  checkAll,
  loader,
  loading,
  handleApprovalRequiredDrawer,
  handleTransactionType,
  handleCountry,
  setSearchApproval,
  setSelectedTransactionList,
  handleRowClick,
  setCheckAll,
  handleBulkReject,
  handleBulkApprove,
}: Readonly<ViewApprovalRequired>) {
  const t = useT();
  const [paymentTypeList, setPaymentTypeList] = useState<PaymentType[]>([]);
  const [countryList, setCountryList] = useState<CountryInfo[]>([]);

  const getPaymentTypeAPI = async () => {
    try {
      if (country) {
        const payload = {
          debitCountryCode2: country,
        };
        const result = await gxPaymentTypesApi(payload);
        if (gxIsArray(result?.data)) {
          setPaymentTypeList(result?.data);
        }
      }
    } catch (err) {
      console.log("Error in getPaymentTypeAPI::", err);
    }
  };

  useEffect(() => {
    getPaymentTypeAPI();
  }, [country]);

  const getCountriesApi = async () => {
    try {
      const result = await gxGetCountryList();

      if (gxIsArray(result?.data)) {
        setCountryList(result?.data);
      }
    } catch (err) {
      console.log("Error in getCountriesApi::", err);
    }
  };

  useEffect(() => {
    getCountriesApi();
  }, []);

  const handleBank = (value: any) => {
    setPaymentTypeList([]);
    handleCountry(value);
  };

  if (!openApprovalRequiredDrawer) return null;

  return (
    <DialogXl
      title={t("approval")}
      subtitle={t("required")}
      open={openApprovalRequiredDrawer}
      handleClose={handleApprovalRequiredDrawer}
      showFooter={false}
    >
      <div className="gx-approve-required-modal">
        <div>
          <div className="gx-approve-required-filter-search-container gx-mt-3">
            <div className="gx-approve-required-filter-wrapper">
              <AppCustomDropDown
                data={countryList}
                name={"countryName"}
                value={"countryCode2"}
                selected={country}
                onChange={handleBank}
                placeholder={t("country")}
              />
              <div className="gx-approve-required-transaction-type">
                <AppCustomDropDown
                  data={paymentTypeList}
                  name={"paymentTypeName"}
                  value={"paymentTypeId"}
                  selected={transactionType}
                  onChange={handleTransactionType}
                  placeholder={t("transaction_type")}
                />
              </div>
            </div>
            <div className="gx-approve-required-search-wrapper">
              <SearchInput
                placeholder={t("search_growx_ref_bene")}
                searchQuery={searchApproval || ""}
                onSearchChange={setSearchApproval}
              />
            </div>
          </div>
          <div className="gx-mt-3">
            {tableListAll?.length > 0 ? (
              <div className="gx-approve-required-table-container">
                <ApproveRequiredTable
                  selectedTransactionList={selectedTransactionList}
                  setSelectedTransactionList={setSelectedTransactionList}
                  tableList={tableListAll}
                  showAll={true}
                  onRowClick={handleRowClick}
                  setCheckAll={setCheckAll}
                  checkAll={checkAll}
                />
              </div>
            ) : (
              <div className="gx-approve-required-do-data">
                <NoDataFound message="No Approval Found" />
              </div>
            )}
          </div>
          <div ref={loader} className="gx-approve-required-loader">
            {loading && <Loader size="medium" type="converging-spinner" />}
          </div>
        </div>
        <div className="gx-dialog-actions gx-footer-drawer-container">
          <GxHorizontalLine />
          <GxRedRejectButton onClick={handleBulkReject}>
            Reject
          </GxRedRejectButton>

          <GxPrimaryButton
            handleSubmit={handleBulkApprove}
            label={t("approve")}
          />
        </div>
      </div>
    </DialogXl>
  );
}
